import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { ChevronUp, ChevronDown, Eye, Activity, AlertCircle, LayoutGrid, User, Image } from 'lucide-react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import useTableSort from '@/shared/hooks/useTableSort';
import { usePagination, paginateData } from '@/shared/components/pagination/index';

import { Layout } from '../types/types';
import { useLayoutHotspots, usePaginatedHotspots } from '../hooks/useLayoutHotspots';
import {
  transformHotspotForTable,
  filterHotspots,
  filterHotspotsByStatus,
  filterHotspotsByCategory,
  filterHotspotsBySubcategory,
  HotspotTableRow,
} from '../utils/hotspotTransformers';

interface LayoutOption extends Layout {
  isAllHotspots?: boolean;
}

interface HotspotsTableProps {
  searchTerm: string;
  statusFilter: string;
  categoryFilter: number | null;
  subcategoryFilter: number | null;
  visibleColumns: Record<string, boolean>;
  selectedLayout: LayoutOption | undefined;
  projectId: string;
  onViewHotspot?: (
    hotspotId: number,
    photoId: number,
    coordinates: { pitch: number; yaw: number }
  ) => void;
}

const HotspotsTable = ({
  searchTerm,
  statusFilter,
  categoryFilter,
  subcategoryFilter,
  visibleColumns,
  selectedLayout,
  projectId,
  onViewHotspot,
}: HotspotsTableProps) => {
  const [sortBy, setSortBy] = useState<string>('layoutPhotoLabel');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  // Pagination state for "All Hotspots" view
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20; // Fixed page size for "All Hotspots" mode

  // Determine if we're in "All Hotspots" mode
  const isAllHotspotsMode = selectedLayout?.isAllHotspots;

  // Fetch data based on the selected view
  const layoutHotspotsQuery = useLayoutHotspots(
    !isAllHotspotsMode ? selectedLayout?.id : undefined,
    projectId,
  );
  const paginatedHotspotsQuery = usePaginatedHotspots(
    isAllHotspotsMode ? projectId : undefined,
    currentPage,
    pageSize,
    isAllHotspotsMode ? searchTerm : undefined
  );

  // Use the appropriate query result
  const {
    hotspots: rawHotspots,
    isLoading,
    error,
    isRefetching,
    metrics,
  } = isAllHotspotsMode
    ? {
        hotspots: paginatedHotspotsQuery.hotspots,
        isLoading: paginatedHotspotsQuery.isLoading,
        error: paginatedHotspotsQuery.error,
        isRefetching: paginatedHotspotsQuery.isFetching,
        metrics: paginatedHotspotsQuery.metrics,
      }
    : {
        hotspots: layoutHotspotsQuery.hotspots,
        isLoading: layoutHotspotsQuery.isLoading,
        error: layoutHotspotsQuery.error,
        isRefetching: layoutHotspotsQuery.isRefetching,
        metrics: layoutHotspotsQuery.metrics,
      };

  // Get pagination data for "All Hotspots" mode
  const paginationData = isAllHotspotsMode ? paginatedHotspotsQuery.data : null;

  // For layout-specific view, apply client-side filtering and pagination
  const layoutViewHotspots = useMemo(() => {
    if (isAllHotspotsMode) return [];

    if (!rawHotspots.length) return [];

    let filtered = filterHotspots(rawHotspots, searchTerm);
    filtered = filterHotspotsByStatus(filtered, statusFilter);
    filtered = filterHotspotsByCategory(filtered, categoryFilter);
    filtered = filterHotspotsBySubcategory(filtered, subcategoryFilter);
    return filtered.map((hotspot) => transformHotspotForTable(hotspot));
  }, [
    rawHotspots,
    searchTerm,
    statusFilter,
    categoryFilter,
    subcategoryFilter,
    isAllHotspotsMode,
  ]);

  // For "All Hotspots", server already filtered by searchTerm; just transform to table format
  const allHotspotsViewHotspots = useMemo(() => {
    if (!isAllHotspotsMode) return [];
    let hotspots = rawHotspots;
    hotspots = filterHotspotsByStatus(hotspots, statusFilter);
    hotspots = filterHotspotsByCategory(hotspots, categoryFilter);
    hotspots = filterHotspotsBySubcategory(hotspots, subcategoryFilter);
    return hotspots.map((hotspot) => transformHotspotForTable(hotspot));
  }, [
    rawHotspots,
    statusFilter,
    categoryFilter,
    subcategoryFilter,
    isAllHotspotsMode,
  ]);

  // Final hotspots list
  const hotspots = isAllHotspotsMode ? allHotspotsViewHotspots : layoutViewHotspots;

  // For layout view, use client-side pagination
  const [layoutPaginationState, layoutPaginationActions] = usePagination(
    layoutViewHotspots.length,
    15
  );

  const finalHotspots = isAllHotspotsMode
    ? allHotspotsViewHotspots // Server handles pagination, but apply client-side search/status filters
    : paginateData(hotspots, layoutPaginationState.currentPage, layoutPaginationState.itemsPerPage); // Client-side for layouts

  // Reset to page 1 when layout/mode or search changes (so search/filter shows from first page).
  useEffect(() => {
    if (isAllHotspotsMode) {
      setCurrentPage(1);
    } else {
      layoutPaginationActions.setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayout, isAllHotspotsMode, searchTerm]);

  const columns = [
    {
      id: 'layoutPhotoLabel',
      header: 'Layout / Photo',
      accessor: (row: HotspotTableRow) => row.layoutPhotoLabel,
      icon: LayoutGrid,
    },
    {
      id: 'title',
      header: 'Title',
      accessor: (row: HotspotTableRow) => row.title,
      icon: Activity,
    },
    {
      id: 'createdBy',
      header: 'Created By',
      accessor: (row: HotspotTableRow) => row.createdBy,
      icon: User,
    },
    {
      id: 'associatedObjectsCount',
      header: 'Associated Objects',
      accessor: (row: HotspotTableRow) => row.associatedObjectsCount,
      icon: Image,
    },
    { id: 'actions', header: '', accessor: () => null },
  ];

  const filterMissingRowData = useCallback((data: string | number | null | undefined) => {
    return data?.toString().trim() || '—';
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(field);
        setSortOrder('asc');
      }
    },
    [sortBy, sortOrder]
  );

  const handleViewHotspot = useCallback(
    (hotspot: HotspotTableRow) => {
      const photoId = hotspot.rawData.photo_id;
      const coordinates = {
        pitch: hotspot.rawData.pitch,
        yaw: hotspot.rawData.yaw,
      };

      const params = new URLSearchParams();
      if (selectedLayout?.id && !selectedLayout.isAllHotspots) {
        params.set('layout', String(selectedLayout.id));
      }
      if (photoId) {
        params.set('photo', String(photoId));
      }
      params.set('hotspot', String(hotspot.id));
      params.set('pitch', String(coordinates.pitch));
      params.set('yaw', String(coordinates.yaw));

      if (onViewHotspot && photoId) {
        // Keep URL shareable so user can copy directly from address bar.
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
        onViewHotspot(parseInt(hotspot.id), photoId, coordinates);
      }
    },
    [onViewHotspot, selectedLayout]
  );

  const sortedHotspots = useTableSort(finalHotspots, columns, sortBy, sortOrder);

  const visibleColumnIds = Object.keys(visibleColumns).filter((id) => visibleColumns[id]);
  const displayColumns =
    visibleColumnIds.length > 0
      ? columns.filter((col) => visibleColumnIds.includes(col.id) || col.id === 'actions')
      : columns;

  // Show full loading only on initial load (no data yet)
  if (isLoading && !paginationData && !layoutHotspotsQuery.hotspots.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-64">
        <LogoLoading variant="breathing" size="lg" message="Loading hotspots..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <p className="text-lg font-medium text-destructive mb-1">Unable to load hotspots</p>
            <p className="text-sm text-muted-foreground">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedLayout) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center">
            <Activity className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <p className="text-lg font-medium text-foreground mb-1">No view selected</p>
            <p className="text-sm text-muted-foreground">Please select a view to see hotspots</p>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced empty state
  if (finalHotspots.length === 0 && !isLoading) {
    const hasFilters = searchTerm || statusFilter !== 'all';
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Activity className="h-6 w-6 text-primary" />
          </div>
          <div>
            <p className="text-lg font-medium text-foreground mb-1">
              {hasFilters ? 'No matching hotspots' : 'No hotspots found'}
            </p>
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? 'Try adjusting your search or filter criteria'
                : isAllHotspotsMode
                  ? "This project doesn't have any hotspots yet"
                  : "This layout doesn't have any hotspots yet"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Top section with metrics and top pagination */}
      <div className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 space-y-4">
        {/* Show refined loading indicator during refetch */}
        {isRefetching && (
          <div className="px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-amber-700 dark:text-amber-400 font-medium">Loading page...</span>
            </div>
          </div>
        )}

        {/* Top pagination for "All Hotspots" - visible only when there are multiple pages */}
        {isAllHotspotsMode && paginationData && Math.ceil(paginationData.count / pageSize) > 1 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border">
            <div className="text-sm text-muted-foreground order-2 sm:order-1">
              <span className="font-medium">
                Page {currentPage} of {Math.ceil(paginationData.count / pageSize)}
              </span>
              <span className="hidden sm:inline"> • </span>
              <span className="block sm:inline">
                Showing {(currentPage - 1) * pageSize + 1} to{' '}
                {Math.min(currentPage * pageSize, paginationData.count)} of {paginationData.count}{' '}
                hotspots
              </span>
              {metrics && (
                <span className="ml-2 text-muted-foreground hidden lg:inline">
                  • {metrics.totalOverlays} overlay{metrics.totalOverlays !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="order-1 sm:order-2 flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => prev - 1)}
                disabled={!paginationData?.previous || isRefetching}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={!paginationData?.next || isRefetching}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Top pagination for layout view */}
        {!isAllHotspotsMode &&
          layoutViewHotspots.length > 0 &&
          Math.ceil(layoutViewHotspots.length / layoutPaginationState.itemsPerPage) > 1 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border">
              <div className="text-sm text-muted-foreground order-2 sm:order-1">
                <span className="font-medium">
                  Page {layoutPaginationState.currentPage} of{' '}
                  {Math.ceil(layoutViewHotspots.length / layoutPaginationState.itemsPerPage)}
                </span>
                <span className="hidden sm:inline"> • </span>
                <span className="block sm:inline">
                  Showing{' '}
                  {(layoutPaginationState.currentPage - 1) * layoutPaginationState.itemsPerPage + 1}{' '}
                  to{' '}
                  {Math.min(
                    layoutPaginationState.currentPage * layoutPaginationState.itemsPerPage,
                    layoutViewHotspots.length
                  )}{' '}
                  of {layoutViewHotspots.length} hotspots
                </span>
              </div>
              <div className="order-1 sm:order-2 flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    layoutPaginationActions.setCurrentPage(layoutPaginationState.currentPage - 1)
                  }
                  disabled={layoutPaginationState.currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    layoutPaginationActions.setCurrentPage(layoutPaginationState.currentPage + 1)
                  }
                  disabled={
                    layoutPaginationState.currentPage >=
                    Math.ceil(layoutViewHotspots.length / layoutPaginationState.itemsPerPage)
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
      </div>

      {/* Scrollable table container - KEY FIX: This creates the scrollable area */}
      <div className="flex-1 min-h-0 px-4 sm:px-6 overflow-y-auto">
        <div className="bg-card rounded-lg border border-border shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  {displayColumns.slice(0, -1).map((column) => {
                    const IconComponent = column.icon;
                    return (
                      <TableHead
                        key={column.id}
                        className="cursor-pointer hover:bg-accent/50 transition-colors px-4 sm:px-6 py-4 font-medium text-foreground text-sm whitespace-nowrap"
                        onClick={() => handleSort(column.id)}
                      >
                        <div className="flex items-center space-x-2">
                          {IconComponent && (
                            <IconComponent className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <span className="hidden sm:inline">{column.header}</span>
                          <span className="sm:hidden">
                            {column.id === 'layoutPhotoLabel'
                              ? 'Layout'
                              : column.id === 'title'
                                ? 'Title'
                                : column.id === 'createdBy'
                                  ? 'By'
                                  : column.id === 'associatedObjectsCount'
                                    ? 'Objects'
                                    : column.header}
                          </span>
                          {sortBy === column.id && (
                            <div className="ml-1">
                              {sortOrder === 'asc' ? (
                                <ChevronUp className="h-4 w-4 text-primary" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-primary" />
                              )}
                            </div>
                          )}
                        </div>
                      </TableHead>
                    );
                  })}
                  <TableHead className="text-right px-4 sm:px-6 py-4 font-medium text-foreground w-16 text-sm">
                    <span className="hidden sm:inline">View</span>
                    <Eye className="h-4 w-4 sm:hidden" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Show skeleton rows when fetching new page in "All Hotspots" mode */}
                {isRefetching && isAllHotspotsMode && paginationData && (
                  <>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <Skeleton className="h-4 w-40" />
                        </TableCell>
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <Skeleton className="h-4 w-28" />
                        </TableCell>
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4 text-right">
                          <Skeleton className="h-7 w-7 rounded-full ml-auto" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {/* Actual data rows */}
                {!isRefetching &&
                  sortedHotspots.map((hotspot: HotspotTableRow, index) => (
                    <TableRow
                      key={hotspot.id}
                      className={`
                      group hover:bg-accent/30 transition-all duration-150 border-border
                      ${hoveredRowId === hotspot.id ? 'bg-primary/5' : ''}
                      ${index % 2 === 0 ? 'bg-card' : 'bg-muted/10'}
                    `}
                      onMouseEnter={() => setHoveredRowId(hotspot.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      {(visibleColumns.layoutPhotoLabel ?? true) && (
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="text-sm text-muted-foreground">
                            <span
                              className="truncate max-w-[180px] sm:max-w-none inline-block"
                              title={hotspot.layoutPhotoLabel}
                            >
                              {filterMissingRowData(hotspot.layoutPhotoLabel)}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {(visibleColumns.title ?? true) && (
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">
                            <span
                              className="truncate max-w-[150px] sm:max-w-none inline-block"
                              title={hotspot.title}
                            >
                              {filterMissingRowData(hotspot.title)}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {(visibleColumns.createdBy ?? true) && (
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            <span
                              className="truncate max-w-[120px] sm:max-w-none inline-block"
                              title={hotspot.createdBy}
                            >
                              {filterMissingRowData(hotspot.createdBy)}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {(visibleColumns.associatedObjectsCount ?? true) && (
                        <TableCell className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="text-sm text-foreground tabular-nums">
                            {hotspot.associatedObjectsCount}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="px-4 sm:px-6 py-3 sm:py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewHotspot(hotspot)}
                          className={`
                          h-7 w-7 sm:h-8 sm:w-8 p-0 rounded-full transition-all duration-200
                          hover:bg-primary/10 hover:shadow-sm
                          group-hover:bg-primary/5 group-hover:text-primary
                          ${hoveredRowId === hotspot.id ? 'bg-primary/10 text-primary shadow-sm' : 'text-muted-foreground'}
                        `}
                          title="View in 360° Photo"
                        >
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Fixed bottom pagination footer */}
      <div className="flex-shrink-0 px-4 sm:px-6 pb-4 sm:pb-6">
        {finalHotspots.length > 0 && (
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border">
            <div className="text-sm text-muted-foreground order-2 sm:order-1">
              {isAllHotspotsMode && paginationData ? (
                <>
                  Showing {(currentPage - 1) * pageSize + 1} to{' '}
                  {Math.min(currentPage * pageSize, paginationData.count)} of {paginationData.count}{' '}
                  hotspots
                </>
              ) : (
                <>
                  Showing{' '}
                  {(layoutPaginationState.currentPage - 1) * layoutPaginationState.itemsPerPage + 1}{' '}
                  to{' '}
                  {Math.min(
                    layoutPaginationState.currentPage * layoutPaginationState.itemsPerPage,
                    layoutViewHotspots.length
                  )}{' '}
                  of {layoutViewHotspots.length} hotspots
                </>
              )}
              {metrics && (
                <span className="ml-2 sm:ml-4 text-muted-foreground hidden sm:inline">
                  • {metrics.totalOverlays} overlay{metrics.totalOverlays !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="order-1 sm:order-2">
              {isAllHotspotsMode &&
              paginationData &&
              Math.ceil(paginationData.count / pageSize) > 1 ? (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                    disabled={!paginationData?.previous || isRefetching}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={!paginationData?.next || isRefetching}
                  >
                    Next
                  </Button>
                </div>
              ) : !isAllHotspotsMode &&
                Math.ceil(layoutViewHotspots.length / layoutPaginationState.itemsPerPage) > 1 ? (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      layoutPaginationActions.setCurrentPage(layoutPaginationState.currentPage - 1)
                    }
                    disabled={layoutPaginationState.currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      layoutPaginationActions.setCurrentPage(layoutPaginationState.currentPage + 1)
                    }
                    disabled={
                      layoutPaginationState.currentPage >=
                      Math.ceil(layoutViewHotspots.length / layoutPaginationState.itemsPerPage)
                    }
                  >
                    Next
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotspotsTable;
